/**
 * The list and form hooks VLM's web apps share (the admin's first; the platform's too). Filters,
 * page, size and order live in the address, so a filtered list can be shared and Back works.
 */
import type { TableSort } from './table';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/** `value`, settled for `ms` — for search boxes that should not query on every key. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return settled;
}

/**
 * A search box over a value kept elsewhere (the address): typing commits after `ms`; a change from
 * outside (the back button, "clear filters") flows back into the box.
 */
export function useSearchBox(committed: string, commit: (value: string) => void, ms = 300) {
  const [text, setText] = useState(committed);
  const last = useRef(committed);
  const latest = useRef(commit);
  latest.current = commit;
  useEffect(() => {
    if (committed !== last.current) {
      last.current = committed;
      setText(committed);
    }
  }, [committed]);
  useEffect(() => {
    if (text === last.current) return;
    const id = setTimeout(() => {
      last.current = text;
      latest.current(text);
    }, ms);
    return () => clearTimeout(id);
  }, [text, ms]);
  return [text, setText] as const;
}

const SIZES = [10, 20, 50, 100];

/** "plateNumber,desc" (the address, and the API's sort=) → a TableSort — only for a key the list sorts by. */
export function parseSort(value: string | null, sortable: readonly string[]): TableSort | null {
  if (!value) return null;
  const comma = value.lastIndexOf(',');
  const key = comma < 0 ? value : value.slice(0, comma);
  const direction = comma < 0 ? 'asc' : value.slice(comma + 1).toLowerCase();
  return sortable.includes(key) && (direction === 'asc' || direction === 'desc') ? { key, direction } : null;
}

/** A TableSort → the API's sort= value (none: the list's own order). */
export const sortParam = (sort: TableSort | null): string | undefined => (sort ? `${sort.key},${sort.direction}` : undefined);

/**
 * A list's filters, page, page size and order in the address, so a filtered list can be shared and
 * the back button works. `page` is 0-based here and 1-based in the address ("?page=2"); `sortable`
 * are the API properties the list may be sorted by ("?sort=plateNumber,desc").
 */
export function useListParams<F extends Record<string, string>>(defaults: F, sortable: readonly string[] = []) {
  const [params, setParams] = useSearchParams();
  const values = { ...defaults };
  for (const key of Object.keys(defaults) as Array<keyof F>) {
    const v = params.get(key as string);
    if (v !== null) values[key] = v as F[keyof F];
  }
  const page = Math.max(0, (Number(params.get('page')) || 1) - 1);
  const sizeParam = Number(params.get('size'));
  const size = SIZES.includes(sizeParam) ? sizeParam : 20;
  const sort = parseSort(params.get('sort'), sortable);

  /** A new order starts again on the first page. */
  const setSort = (next: TableSort | null) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        const value = sortParam(next);
        if (value) p.set('sort', value);
        else p.delete('sort');
        p.delete('page');
        return p;
      },
      { replace: true },
    );

  /** Set filters (any change but the page itself returns to the first page). */
  const set = (patch: Partial<F> & { page?: number; size?: number }) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(patch)) {
          if (key === 'page') continue;
          const isDefault = key === 'size' ? value === 20 : value === defaults[key];
          if (value === undefined || value === null || value === '' || isDefault) next.delete(key);
          else next.set(key, String(value));
        }
        if (patch.page !== undefined && patch.page > 0) next.set('page', String(patch.page + 1));
        else next.delete('page');
        return next;
      },
      { replace: true },
    );

  const filtered = Object.keys(defaults).some((k) => values[k as keyof F] !== defaults[k as keyof F]);
  return { values, page, size, set, filtered, params, setParams, sort, setSort };
}

/** A number from the address, or null. */
export const numberParam = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** The router `state` a link to a form page carries: the page it is opened from, as it is now (useWayBack reads it). */
export function useHere(): { from: string } {
  const { pathname, search } = useLocation();
  return { from: `${pathname}${search}` };
}

export interface WayBack {
  /** The back link's props: where it goes, and a plain click that steps back instead when the parent opened the form. */
  link: { to: string; onClick: (event: MouseEvent<HTMLAnchorElement>) => void };
  /** Leave the form: Cancel, or saved. */
  leave: () => void;
}

/**
 * A form page's way back to `parent` (operator, 2026-10-24: every form is a page of its own, none in a
 * popup). Opened from the parent (`state.from`, set by a link with useHere's state), it returns there as
 * the reader left it — filters, page, order, tab — by one step back, so the form leaves the history as
 * a closed dialog did. Opened any other way (an address typed or shared, a new tab), it goes to `parent`
 * in the form's place.
 */
export function useWayBack(parent: string): WayBack {
  const navigate = useNavigate();
  const { state } = useLocation();
  const from = (state as { from?: unknown } | null)?.from;
  const pathOf = (to: string) => to.split(/[?#]/)[0];
  const opened = typeof from === 'string' && pathOf(from) === pathOf(parent);
  const to = opened ? (from as string) : parent;
  return {
    link: {
      to,
      onClick: (event) => {
        // a click for a new tab or window keeps the link's own way
        if (!opened || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        navigate(-1);
      },
    },
    leave: () => (opened ? navigate(-1) : navigate(to, { replace: true })),
  };
}

/** A boolean kept in localStorage (a panel left open, a layer left on). */
export function useStoredFlag(key: string, initial: boolean): [boolean, (value: boolean) => void] {
  const [value, setValue] = useState(() => {
    try {
      const v = localStorage.getItem(key);
      return v === null ? initial : v === '1';
    } catch {
      return initial;
    }
  });
  const set = (next: boolean) => {
    setValue(next);
    try {
      localStorage.setItem(key, next ? '1' : '0');
    } catch {
      // storage blocked: kept for this page
    }
  };
  return [value, set];
}
